import { Send, Undo2 } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { AdminAuction, AdminAuctionOffer } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useSecondChance, useWithdrawOffer } from '../hooks/use-auction-mutations';
import { useAuctionOffers } from '../hooks/use-auction-offers';

const DEADLINES = ['24', '48', '72'] as const;

/** The reasons the API gives for refusing an offer, said in the store's terms. */
const REFUSALS: Record<string, string> = {
  winner_paid: 'auctions:secondChance.refused.winnerPaid',
  no_bidders_left: 'auctions:secondChance.refused.noBiddersLeft',
  offer_pending: 'auctions:secondChance.refused.offerPending',
  not_ended: 'auctions:secondChance.refused.notEnded',
};

const STATUS_VARIANT: Record<AdminAuctionOffer['status'], 'default' | 'secondary' | 'outline'> = {
  pending: 'default',
  accepted: 'secondary',
  declined: 'outline',
  expired: 'outline',
  withdrawn: 'outline',
};

function SendOfferDialog({
  storeId,
  auction,
  open,
  onOpenChange,
}: {
  storeId: string;
  auction: AdminAuction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['auctions', 'common']);
  const send = useSecondChance(storeId, auction.id);
  const message = useErrorMessage();
  const [hours, setHours] = useState<(typeof DEADLINES)[number]>('48');
  const fieldId = useId();
  const reason = send.error instanceof ApiError ? send.error.meta.reason : undefined;
  const refusal = typeof reason === 'string' ? REFUSALS[reason] : undefined;
  const unpaidWinner = auction.orderId !== null && auction.soldVia !== null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) send.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('auctions:secondChance.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {unpaidWinner
              ? t('auctions:secondChance.confirmUnpaid')
              : t('auctions:secondChance.confirmNoSale')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor={fieldId}>{t('auctions:secondChance.deadline')}</Label>
          <Select value={hours} onValueChange={(value) => setHours(value as typeof hours)}>
            <SelectTrigger id={fieldId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEADLINES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t('auctions:secondChance.hours', { count: Number(value) })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {send.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{refusal ? t(refusal) : message(send.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={send.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={send.isPending}
            onClick={(event) => {
              event.preventDefault();
              send.mutate(Number(hours), { onSuccess: () => onOpenChange(false) });
            }}
          >
            {send.isPending ? t('auctions:actions.working') : t('auctions:secondChance.send')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * After the close: offer the lot to the next bidder when the winner did not pay (or the reserve
 * was not met), follow the offer and withdraw it. Only ended auctions show it.
 */
export function SecondChanceCard({
  storeId,
  auction,
  canWrite,
}: {
  storeId: string;
  auction: AdminAuction;
  canWrite: boolean;
}) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const [open, setOpen] = useState(false);
  const offers = useAuctionOffers(storeId, auction.id, auction.status === 'ended');
  const withdraw = useWithdrawOffer(storeId, auction.id);
  const pending = auction.secondChance?.status === 'pending';
  const bidders = auction.bidderCount;

  return (
    <Card className="gap-3" data-tour="auctions.detail.second-chance">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>{t('secondChance.title')}</CardTitle>
        {canWrite && !pending && bidders > 0 ? (
          <Can permission="auctions.write">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
              <Send aria-hidden />
              {t('secondChance.open')}
            </Button>
          </Can>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">{t('secondChance.description')}</p>
        {offers.data && offers.data.length > 0 ? (
          <ul className="flex flex-col gap-2" aria-label={t('secondChance.list')}>
            {offers.data.map((offer) => (
              <li key={offer.id} className="flex flex-col gap-1 rounded-md border p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{offer.customerName ?? offer.customerEmail}</span>
                  <Badge variant={STATUS_VARIANT[offer.status]}>
                    {t(`secondChance.status.${offer.status}`)}
                  </Badge>
                </div>
                <span className="text-muted-foreground">
                  {[
                    offer.bidderNumber !== null
                      ? t('bidder.number', { number: offer.bidderNumber })
                      : null,
                    format.money(offer.amount),
                    offer.status === 'pending'
                      ? t('secondChance.until', { when: format.dateTime(offer.expiresAt) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {offer.status === 'pending' && canWrite ? (
                  <Can permission="auctions.write">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="self-start"
                      disabled={withdraw.isPending}
                      onClick={() => withdraw.mutate(offer.id)}
                    >
                      <Undo2 aria-hidden />
                      {t('secondChance.withdraw')}
                    </Button>
                  </Can>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-xs">
            {bidders > 0 ? t('secondChance.none') : t('secondChance.noBidders')}
          </p>
        )}
      </CardContent>
      {canWrite ? (
        <SendOfferDialog storeId={storeId} auction={auction} open={open} onOpenChange={setOpen} />
      ) : null}
    </Card>
  );
}
